import { useCallback, useEffect, useRef, useState } from 'react';

export type InsightsMessageType = 'human' | 'ai' | string;

export interface InsightsMessage {
  id: string;
  type: InsightsMessageType;
  content: unknown;
  metadata?: Record<string, unknown>;
  sql_query?: string;
}

export interface UseInsightsStreamOptions {
  apiUrl: string;
  threadId: string | null;
  onThreadId: (id: string | null) => void;
  onThreadCreated?: () => void;
  headers?: () => Record<string, string>;
}

export interface UseInsightsStreamResult {
  messages: InsightsMessage[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  isIncomplete: boolean;
  error: { message?: string } | null;
  submit: (input: { messages: { type: string; content: unknown }[]; webSearchEnabled?: boolean }) => void;
  stop: () => void;
  threadId: string | null;
  ownerAuthServiceUuid: string | null;
}

const createFallbackMessageId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function useInsightsStream(options: UseInsightsStreamOptions): UseInsightsStreamResult {
  const { apiUrl, threadId, onThreadId, onThreadCreated, headers } = options;

  const [messages, setMessages] = useState<InsightsMessage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [isIncomplete, setIsIncomplete] = useState<boolean>(false);
  const [ownerAuthServiceUuid, setOwnerAuthServiceUuid] = useState<string | null>(null);
  const [error, setError] = useState<{ message?: string } | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const currentThreadIdRef = useRef<string | null>(threadId);
  const previousThreadIdRef = useRef<string | null>(threadId);
  const loadedHistoryForThread = useRef<Set<string>>(new Set());
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(threadId);
  const wasLoadingRef = useRef<boolean>(false);
  const draftMessageIdRef = useRef<string | null>(null);
  const draftSourceIdRef = useRef<string | null>(null);

  useEffect(() => {
    const prevThreadId = previousThreadIdRef.current;
    previousThreadIdRef.current = threadId;
    currentThreadIdRef.current = threadId;
    setCurrentThreadId(threadId);

    // Only clear messages when:
    // - Switching between existing threads (prevThreadId !== null && threadId !== null && different)
    // - Going from an existing thread to null (clicking "New")
    // Do NOT clear when going from null to a new thread (fresh conversation being persisted)
    const isNewConversationBeingPersisted = prevThreadId === null && threadId !== null;
    const isSwitchingToNewConversation = threadId === null;
    const isSwitchingBetweenThreads = prevThreadId !== null && threadId !== null && prevThreadId !== threadId;

    // Only clear messages when going to a fresh conversation (clicking "New")
    // When switching between threads, let loadHistory replace messages to avoid flash
    if (isSwitchingToNewConversation) {
      setMessages([]);
      setIsIncomplete(false);
      setOwnerAuthServiceUuid(null);
      setError(null);
      draftMessageIdRef.current = null;
      draftSourceIdRef.current = null;
    } else if (isSwitchingBetweenThreads) {
      // Just reset draft refs, messages will be replaced by loadHistory
      draftMessageIdRef.current = null;
      draftSourceIdRef.current = null;
      setIsIncomplete(false);
      setError(null);
    }

    // Clear history cache for this thread to force reload when switching to existing thread
    if (threadId && !isNewConversationBeingPersisted) {
      loadedHistoryForThread.current.delete(threadId);
    }
  }, [threadId]);

  const extractTextFromContentBlocks = useCallback((content: unknown): string => {
    if (!Array.isArray(content)) {
      return '';
    }

    return content
      .map((block: unknown) => {
        if (!block || typeof block !== 'object') {
          return '';
        }

        const b = block as { type?: string; text?: unknown };
        if (b.type === 'text' && typeof b.text === 'string') {
          return b.text;
        }

        return '';
      })
      .join('');
  }, []);

  const hasToolUseBlocks = useCallback((content: unknown): boolean => {
    if (!Array.isArray(content)) {
      return false;
    }

    return content.some((block: unknown) => {
      const b = block as { type?: string } | undefined;
      return b?.type === 'tool_use';
    });
  }, []);

  const getHeaders = useCallback((): Record<string, string> => {
    const base = headers ? headers() : {};
    return { ...base };
  }, [headers]);

  const ensureThreadId = useCallback(async (): Promise<string> => {
    if (currentThreadIdRef.current) {
      return currentThreadIdRef.current;
    }

    const response = await fetch(`${apiUrl}/insights/threads`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to create insights thread');
    }

    const data: { threadId: string } = await response.json();
    currentThreadIdRef.current = data.threadId;
    setCurrentThreadId(data.threadId);
    onThreadId(data.threadId);
    // Immediately refresh sidebar to show new thread
    onThreadCreated?.();
    return data.threadId;
  }, [apiUrl, getHeaders, onThreadCreated, onThreadId]);

  const stop = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }

    setIsLoading(false);
  }, []);

  const loadHistory = useCallback(
    async (threadId: string, signal?: AbortSignal) => {
      if (loadedHistoryForThread.current.has(threadId)) {
        return;
      }

      setIsLoadingHistory(true);
      try {
        const response = await fetch(`${apiUrl}/insights/threads/${threadId}/history`, {
          method: 'GET',
          headers: {
            ...getHeaders(),
          },
          signal,
        });

        if (!response.ok) {
          return;
        }

        const data: {
          messages?: { id?: string; type: string; content: unknown; sql_query?: string }[];
          isIncomplete?: boolean;
          ownerAuthServiceUuid?: string | null;
        } = await response.json();

        if (typeof data.ownerAuthServiceUuid === 'string') {
          setOwnerAuthServiceUuid(data.ownerAuthServiceUuid);
        } else {
          setOwnerAuthServiceUuid(null);
        }

        if (Array.isArray(data.messages)) {
          // Filter out AI messages that only contain tool_use blocks (internal)
          const filtered = data.messages.filter((m) => {
            if (m.type !== 'ai') return true;
            if (!Array.isArray(m.content)) return true;
            // If all content blocks are tool_use, filter out
            const hasNonToolUse = m.content.some((block: unknown) => {
              const b = block as { type?: string } | undefined;
              return b?.type !== 'tool_use';
            });
            return hasNonToolUse;
          });

          const mapped: InsightsMessage[] = filtered.map((m) => ({
            id: m.id ?? createFallbackMessageId(),
            type: m.type,
            content: m.content,
            ...(m.sql_query ? { sql_query: m.sql_query } : {}),
          }));
          setMessages(mapped);
          setIsIncomplete(data.isIncomplete ?? false);
        }

        loadedHistoryForThread.current.add(threadId);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }

        // Silently fail
      } finally {
        setIsLoadingHistory(false);
      }
    },
    [apiUrl, getHeaders],
  );

  useEffect(() => {
    if (!threadId) {
      return;
    }

    const abortController = new AbortController();
    void loadHistory(threadId, abortController.signal);
    return () => {
      abortController.abort();
    };
  }, [loadHistory, threadId]);

  const stream = useCallback(
    async (threadId: string, text: string, webSearchEnabled: boolean) => {
      if (!text.trim()) {
        return;
      }

      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${apiUrl}/insights/threads/${threadId}/stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getHeaders(),
          },
          body: JSON.stringify({
            text,
            web_search_enabled: webSearchEnabled,
          }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error('Failed to start insights stream');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let doneReading = false;

        while (!doneReading) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          let separatorIndex = buffer.indexOf('\n\n');
          while (separatorIndex !== -1) {
            const rawEvent = buffer.slice(0, separatorIndex);
            buffer = buffer.slice(separatorIndex + 2);
            separatorIndex = buffer.indexOf('\n\n');

            const dataPayload = rawEvent
              .split('\n')
              .filter((line) => line.startsWith('data:'))
              .map((line) => line.slice(5).trimStart())
              .join('\n')
              .trim();

            if (!dataPayload) {
              continue;
            }

            let parsed: unknown;
            try {
              parsed = JSON.parse(dataPayload);
            } catch {
              continue;
            }

            if (!parsed || typeof parsed !== 'object' || !('event' in parsed)) {
              continue;
            }

            const event = parsed as {
              event: string;
              data?: unknown;
              metadata?: Record<string, unknown>;
            };

            if (event.event === 'message' && event.data && typeof event.data === 'object') {
              const data = event.data as { id?: string; type: string; content: unknown; sql_query?: string };
              const metadata = event.metadata;
              const node = typeof metadata?.langgraph_node === 'string' ? metadata.langgraph_node : null;

              // Stream final assistant output token-by-token:
              // - LangGraph emits AIMessageChunk events as type 'aichunk' with text blocks.
              // - We keep tool-related 'aichunk' events for the tool panel.
              // - For text-only chunks, we coalesce them into a single draft 'ai' message.
              if (data.type === 'aichunk') {
                const hasToolUse = hasToolUseBlocks(data.content);
                const chunkText = extractTextFromContentBlocks(data.content);

                if (!hasToolUse && chunkText && (node === 'data_analyst_node' || node === 'present_results')) {
                  const sourceId = data.id ?? 'stream';
                  const nextDraftId = `draft:${sourceId}`;
                  draftMessageIdRef.current = nextDraftId;
                  draftSourceIdRef.current = sourceId;

                  setMessages((prev) => {
                    const existingIndex = prev.findIndex((msg) => msg.id === nextDraftId);
                    if (existingIndex === -1) {
                      return [
                        ...prev,
                        {
                          id: nextDraftId,
                          type: 'ai',
                          content: chunkText,
                          metadata,
                        },
                      ];
                    }

                    const existing = prev[existingIndex];
                    const existingText = typeof existing.content === 'string' ? existing.content : '';
                    const updated = {
                      ...existing,
                      content: `${existingText}${chunkText}`,
                      metadata,
                    };

                    const next = [...prev];
                    next[existingIndex] = updated;
                    return next;
                  });

                  continue;
                }

                // Keep tool-related chunks for the tool feedback panel.
                setMessages((prev) => [
                  ...prev,
                  {
                    id: data.id ?? createFallbackMessageId(),
                    type: data.type,
                    content: data.content,
                    metadata,
                  },
                ]);
                continue;
              }

              if (data.type === 'ai') {
                const nextId = data.id ?? createFallbackMessageId();
                const draftId = draftMessageIdRef.current;
                const draftSourceId = draftSourceIdRef.current;

                setMessages((prev) => {
                  const withoutDraft =
                    draftId && draftSourceId && nextId !== draftId && nextId !== draftSourceId
                      ? prev.filter((msg) => msg.id !== draftId)
                      : prev;

                  return [
                    ...withoutDraft,
                    {
                      id: nextId,
                      type: data.type,
                      content: data.content,
                      metadata,
                    },
                  ];
                });

                draftMessageIdRef.current = null;
                draftSourceIdRef.current = null;
                continue;
              }

              setMessages((prev) => [
                ...prev,
                {
                  id: data.id ?? createFallbackMessageId(),
                  type: data.type,
                  content: data.content,
                  metadata,
                  ...(data.sql_query ? { sql_query: data.sql_query } : {}),
                },
              ]);
            } else if (event.event === 'end') {
              setIsIncomplete(false);
              doneReading = true;
              break;
            } else if (event.event === 'error') {
              const errorData = event.data as { message?: string } | undefined;
              if (errorData && typeof errorData.message === 'string') {
                setError({ message: errorData.message });
              } else {
                setError({ message: 'Unknown error from stream' });
              }
            }
          }
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }

        if (err instanceof Error) {
          setError({ message: err.message });
        } else {
          setError({ message: String(err) });
        }
      } finally {
        setIsLoading(false);
        controllerRef.current = null;
      }
    },
    [apiUrl, extractTextFromContentBlocks, getHeaders, hasToolUseBlocks],
  );

  useEffect(() => {
    if (wasLoadingRef.current && !isLoading && currentThreadId) {
      onThreadCreated?.();
    }

    wasLoadingRef.current = isLoading;
    // Only trigger when loading transitions from true to false
  }, [currentThreadId, isLoading, onThreadCreated]);

  const submit = useCallback(
    (input: { messages: { type: string; content: unknown }[]; webSearchEnabled?: boolean }) => {
      const lastMessage = input.messages[input.messages.length - 1];
      if (!lastMessage || typeof lastMessage.content !== 'string') {
        return;
      }

      const text = lastMessage.content;
      const webSearchEnabled = input.webSearchEnabled ?? false;
      if (!text.trim() || isLoading) {
        return;
      }

      const optimistic: InsightsMessage = {
        id: createFallbackMessageId(),
        type: 'human',
        content: text,
      };

      setMessages((prev) => [...prev, optimistic]);

      (async () => {
        try {
          const id = await ensureThreadId();
          await stream(id, text, webSearchEnabled);
        } catch (err) {
          if (err instanceof Error) {
            setError({ message: err.message });
          } else {
            setError({ message: String(err) });
          }

          setIsLoading(false);
        }
      })();
    },
    [ensureThreadId, isLoading, stream],
  );

  return {
    messages,
    isLoading,
    isLoadingHistory,
    isIncomplete,
    error,
    submit,
    stop,
    threadId: currentThreadId,
    ownerAuthServiceUuid,
  };
}
